<template>
    <modal-centered-base
    fullScreenDialog
    contentWithNoPadding
    modalWidth="448px"
    rightButtonIcon="check-line"
    applyShadowOnContent
    :titleIcon="sendType === 'email' ? 'letter': 'chat'"
    :title="sendType === 'email' ? t('modals.sendInfo.emailTitle') : t('modals.sendInfo.smsTitle')"
    :rightButtonText="t('modals.sendInfo.sendBtn')"
    :tabs="tabs"
    @tabClicked="clickTabHandler"
    @hideMe="emit('hideMe')"
    @rightButtonClicked="selectedTab === 'me' ? sendMeInfoHandler() : validateDestinationValues = true">
        <!-- Tabs -->
        <tabs-generic v-if="!getMobileAppState" :tabsList="tabs" @clicked="clickTabHandler"/>
        <!-- Content -->
        <transition name="swap" mode="out-in">
            <div class="wrapper-for-shadow" :class="{ 'apply-shadow': applyShadowTop && selectedTab !== 'me', 'mobile': getMobileAppState }">
                <div
                ref="contentWrapperRef"
                class="content-wrapper"
                :style="{
                    'height': selectedTab === 'me' || getMobileAppState ? 'auto' : contentWrapperHeight,
                    'padding-bottom': applyShadowTop && destinationValues.length < 5 ? '16px' : '0'
                }"
                :key="selectedTab"
                :class="{ 'mobile': getMobileAppState }">
                    <infobox-generic v-if="sendType === 'sms'" :label="onlySendIsAvailableForThisOperation ? t('modals.sendInfo.smsCostNote2') : t('modals.sendInfo.smsCostNote')"/>
                    <text-block-generic v-if="selectedTab === 'me'" :params="sendMeInfoParams"/>
                    <!-- Others Tab Content -->
                    <component
                    v-else
                    :is="sendTypeComponent"
                    :destinationValues="destinationValues"
                    :validateDestinationValues="validateDestinationValues"
                    @updateDestinationValue="[updateDestinationValueHandler($event), calcMaxHeightAndShadows()]"
                    @removeDestinationValue="removeDestinationValueHandler"
                    @resetValidationTriggerProp="validateDestinationValues = false"
                    @sendInfo="sendOthersInfoHandler"
                    @calcHeight="calcMaxHeightAndShadows()"/>
                    <!-- Add More Inputs Button -->
                    <button-secondary
                    v-if="selectedTab === 'others' && showAddButton"
                    icon="plus"
                    buttonSize="small"
                    @clicked="destinationValues.push('')"/>
                </div>
            </div>
        </transition>
        <!-- Loader -->
        <loader-generic v-if="showLoader" isOverlay/>
        <!-- Toast -->
        <toast-generic
        v-if="showFeedbackToast"
        :toastType="feedbackToastData.toastType"
        :feedbackText="feedbackToastData.text"
        :buttonsParams="feedbackToastData.buttons"
        @hideMe="showFeedbackToast = false"/>
    </modal-centered-base>
    <!-- Contacts Screen -->
    <!-- NOTE i dont see this on design, but it wil probably be needed -->
    <!-- <modal-side-contacts
    v-if="getMobileAppState && showContacts"
    icon="close"
    :title="t('modals.contacts.title')"
    :sendType="sendType"
    :destinationValues="destinationValues"
    @contactSelected="contactSelectedHandler"
    @hideMe="showContacts = false"/> -->
</template>

<script setup>
import ModalCenteredBase from '@/components/generic/modals/centered/Base'
// import ModalSideContacts from '@/components/generic/modals/side/contacts/Base'
import TabsGeneric from '@/components/generic/tabs/Generic'
import InfoboxGeneric from '@/components/generic/infobox/Generic'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import ButtonSecondary from '@/components/generic/buttons/Secondary'
import LoaderGeneric from '@/components/generic/loaders/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'
/* Vue & Assets */
import { ref, computed, defineAsyncComponent, watch, onBeforeUnmount } from 'vue'
import { errorObjectConstructor, sucessObjectConstrutor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'
/* Store */
import store from '@/store/store'
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
/* i18n */
import { useI18n } from 'vue-i18n'
import { nextTick } from 'process'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['hideMe'])

/* Props
@sendType {String} - send information type ['email'(default), 'sms']
@emailAction {String} - email action function name
@smsAction {String} - sms action function name
@payload { Object} this will depend on action passed
@onlySendIsAvailableForThisOperation {Boolean} prop to fix sms infobox message
*/
const props = defineProps({
	sendType: { type: String, default: 'email' },
    emailAction: String,
    smsAction: String,
    payload: Object,
    onlySendIsAvailableForThisOperation: Boolean
})

const { updatePreventMobileFeatureBack, updatePreventMobileSideModalBack } = mapActions()
const { getMobileAppState } = mapGetters()

const sendTypeComponent = computed(() => {
    const componentName = props.sendType === 'email' ? 'EmailContent' : 'SmsContent'
    return defineAsyncComponent(() => import('@/components/generic/modals/centered/sendInfo/' + componentName))
})

const showLoader = ref(false)
const validateDestinationValues = ref(false)

/*************************************************
*                    Tabs Logic                  *
*************************************************/
const selectedTab = computed(() => { return tabs.value.find(obj => obj.selected).id })

const tabs = ref([
    { id: 'others', label: t('modals.sendInfo.othersTab'), selected: true },
    { id: 'me', label: t('modals.sendInfo.meTab') }
])

const clickTabHandler = $event => {
    tabs.value.forEach(tab => {
		tab.selected = tab.id === $event
	})
}

/*************************************************
*                                                *
*                  SendMe Tab Logic              *
*                                                *
*************************************************/
/* mapActions, mapGetters */
// const { } = mapActions(moduleMapper.SHARE_AND_SEND_INFO)
const { getAllCustomerInfo } = mapGetters(moduleMapper.SESSION)

const currentMeDestination = computed(() => {
    return props.sendType === 'email' ? getAllCustomerInfo.value?.customer.email : getAllCustomerInfo.value?.customer.msisdn
})

const sendMeInfoParams = ref({
    label: props.sendType === 'email' ? t('modals.sendInfo.myEmailLabel') : t('modals.sendInfo.myPhoneLabel'),
    content: currentMeDestination.value
})

const sendMeInfoHandler = async () => {
    showLoader.value = true

    const payload = {
        ...props.payload,
        data: {
            ...props.payload.data
        }
    }

    try {
        const res = await store.dispatch(`shareAndSendInfo/${props.sendType === 'email' ? props.emailAction : props.smsAction}`, payload)
        successHandler(res.data)
    } catch (error) {
        errorHandler(error.response.data)
    }
}

/*************************************************
*                                                *
*               SendOthers Tab Logic             *
*                                                *
*************************************************/
const INITIAL_HEIGHT = props.sendType === 'email' ? '144px' : '262px'
const destinationValues = ref([''])
const contentWrapperRef = ref('')
const contentWrapperHeight = ref(INITIAL_HEIGHT)
const applyShadowTop = ref(false)
const showAddButton = ref(true)

watch(() => destinationValues.value.length, (newVal, oldVal) => {
    nextTick(() => {
        calcMaxHeightAndShadows()
    })
})

watch(() => validateDestinationValues.value, () => {
    nextTick(() => {
        calcMaxHeightAndShadows()
    })
})

const calcMaxHeightAndShadows = () => {
    const destinationValuesLength = destinationValues.value.length
    showAddButton.value = destinationValuesLength < 5

    if (getMobileAppState.value) return
    const emailHeightCalc = `${((destinationValuesLength * 82) + 20 + (destinationValuesLength < 5 ? 48 : -14))}px` /* some magic numbers in here, i know D: */
    const smsHeightCalc = `${((destinationValuesLength * 82) + 20 + (destinationValuesLength < 5 ? 48 : -14)) + 100}px` /* some magic numbers in here, i know D: */

    /* Calc available space */
    const maxHeightPossible = window.innerHeight - 276 /* This value is all other paddings and elements height combined */

    contentWrapperHeight.value = destinationValuesLength === 1 ? INITIAL_HEIGHT : (props.sendType === 'email' ? emailHeightCalc : smsHeightCalc)

    if (maxHeightPossible < parseInt(contentWrapperHeight.value)) contentWrapperHeight.value = `${maxHeightPossible}px`

    /* We need to apply this after the animation ends */
    setTimeout(() => {
        applyShadowTop.value = contentWrapperRef.value.scrollHeight > contentWrapperRef.value.clientHeight
    }, 300)
}

const updateDestinationValueHandler = ({ index, value }) => {
    destinationValues.value[index] = value
}
const removeDestinationValueHandler = index => {
    destinationValues.value.splice(index, 1)
    if (!destinationValues.value.length) {
        destinationValues.value = ['']
    }
}

const sendOthersInfoHandler = () => {
    var requests = destinationValues.value.map(destinationValue => {
        const destination = destinationValue.isPhoneContact ? destinationValue.contactData : destinationValue
        const payload = {
            ...props.payload,
            data: {
                ...props.payload.data,
                [props.sendType === 'email' ? 'email' : 'phoneNumber']: destination
            }
        }
        return store.dispatch(`shareAndSendInfo/${props.sendType === 'email' ? props.emailAction : props.smsAction}`, payload)
    })

    showLoader.value = true
    Promise.all([...requests]).then((response) => {
        successHandler(response[0].data)
    }).catch((error) => {
        errorHandler(error.response.data)
    })
}

/*************************************************
*                                                *
*               Contacts List Logic              *
*                                                *
*************************************************/
const showContacts = ref(false)
const contactToUpdate = ref(null)

const contactsClickHandler = (ev) => {
    contactToUpdate.value = ev
    showContacts.value = true
}

const contactSelectedHandler = (data) => {
    const selectedContact = {
        isPhoneContact: true,
        id: data.contact.id,
        firstName: data.contact.firstName,
        middleName: data.contact.middleName,
        familyName: data.contact.familyName,
        imageData: data.contact.imageData ? data.contact.imageData : false,
        contactData: data.contactData,
        errorState: false
    }

    // Add selected contact to array
    destinationValues.value[contactToUpdate.value] = selectedContact
    showContacts.value = false
}

/*************************************************
*                                                *
*               Feedback Toast Logic             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const successHandler = data => {
    showLoader.value = false

    feedbackToastData.value = sucessObjectConstrutor(data.message)
    showFeedbackToast.value = true
}

const errorHandler = (data, retryFn) => {
    showLoader.value = false

	feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

/***********************************************************
*                                                          *
*                 MOBILE NATIVE BACK LOGIC                 *
*                                                          *
***********************************************************/
/* Preving mobile back to trigger features close */
updatePreventMobileFeatureBack(true)
updatePreventMobileSideModalBack(true)
onBeforeUnmount(() => {
	updatePreventMobileFeatureBack(false)
    updatePreventMobileSideModalBack(false)
})

const triggerClose = ref(false)
const onBackKeyDown = () => {
    triggerClose.value = true
}

document.addEventListener('backbutton', onBackKeyDown, false)
</script>

<style lang="scss" scoped>
.generic-tabs-wrapper {
    width: calc(100% - 48px);
}

.wrapper-for-shadow {
    width: 100%;
    height: fit-content;

    overflow: hidden;
    position: relative;

    &.apply-shadow {
        &:after, &::before {
            content: '';
            width: 100%;
            height: 16px;

            position: absolute;
            left: 0;
            z-index: 1;
        }

        &:before {
            top: -16px;
            box-shadow: var(--box-shadow-1-inverted);
        }

        &:after {
            bottom: -16px;
            box-shadow: var(--box-shadow-1);
        }
    }

    &.mobile {
        padding-bottom: 24px;
    }
}

.content-wrapper {
    @include generic-scrollbar(transparent, var(--primary));

    width: 100%;
    max-height: 700px;
    padding: 16px 24px 0 24px;

    opacity: 1;
    transition: height 0.3s ease-in-out;
    position: relative;

    &.mobile {
        padding: 16px 0 0 0;
    }

    .apply-shadow {

        box-shadow: inset var(--box-shadow-1-inverted), inset var(--box-shadow-1);
    }

    &.swap-enter-active,
    &.swap-leave-active {
        transition: all 0.3s ease-in-out;
    }

    &.swap-enter-from,
    &.swap-leave-to {
        opacity: 0;
        max-height: 128px;
    }

    :deep(.infobox-generic-wrapper) {
        margin-bottom: 16px;
    }
}
</style>
